// Asystent - jeden czat (`/api/czat`, docs/ai/PROJEKT.md 6.4, PLAN-ZROWNANIA etap 5): rozmowy, limity i przykładowe
// wpisy bazy jako stan poza Reactem (jak api/plany.ts). Odpowiedź pisze doradca - tekst na żywo przychodzi zdarzeniem
// SSE `konto` rodzaj `czat.tekst` (cały dotychczasowy tekst), zamiast odpytywania starej APP. Filtr, limity, baza
// i AI liczy worker - APP wyświetla.
import { useSyncExternalStore } from "react";
import type {
  AccountEvent, ChatCycle, ChatCyclesReply, ChatEntry, ChatFilm, ChatIn, ChatLimit, ChatLimitReply, ChatLive, ChatQuestion,
  ChatReply, ChatSearchReply, ChatSent, ChatTopic, HelpReply, OKReply,
} from "../gen/kontrakt";
import { BladZaplecza, zapytanie } from "./zaplecze";

interface Stan {
  wczytane: boolean;
  imie: string;
  pytania: readonly ChatQuestion[];
  /** Limity z chwilą odczytu - APP przelicza „reset_s” na godzinę zegarową. */
  limit: ChatLimit | null;
  limitOd: number;
  przyklady: readonly ChatEntry[];
  /** Tematy bazy wiedzy (#15), filmy przypięte do wpisów (#18), opis cykli dołączonych do pytań (#16). */
  tematy: readonly ChatTopic[];
  filmy: readonly ChatFilm[];
  przebiegi: readonly ChatCycle[];
}

const pusty: Stan = { wczytane: false, imie: "", pytania: [], limit: null, limitOd: 0, przyklady: [], tematy: [], filmy: [], przebiegi: [] };
/** Ostatnie szukanie w rozmowach i temat - odświeżenie po zdarzeniu trzyma ten sam widok. */
let filtr = { szukaj: "", temat: "" };
let stan: Stan = pusty;
const sluchacze = new Set<() => void>();
let wczytywanie: Promise<void> | null = null;

function ustaw(s: Stan): void {
  stan = s;
  for (const f of sluchacze) f();
}

export function useCzat(): Stan {
  return useSyncExternalStore((f) => { sluchacze.add(f); return () => sluchacze.delete(f); }, () => stan);
}

/** Rozmowy (z szukaniem w pytaniach i odpowiedziach - #14), limity, imię, losowe przykładowe wpisy tematu, tematy,
 *  filmy i cykle rozmów (przy każdym wejściu na ekran - jak stara APP). Bez argumentu - ostatni filtr. */
export function wczytajCzat(f?: { szukaj: string; temat: string }): Promise<void> {
  if (f) {
    filtr = f;
    wczytywanie = null;
  }
  const q = new URLSearchParams({ szukaj: filtr.szukaj, temat: filtr.temat });
  wczytywanie ??= zapytanie<ChatReply>("GET", "/api/czat?" + q.toString())
    .then((o) => ustaw({ wczytane: true, imie: o.imie, pytania: o.pytania, limit: o.limit, limitOd: Date.now(),
      przyklady: o.przyklady, tematy: o.tematy, filmy: o.filmy, przebiegi: o.przebiegi }))
    .finally(() => { wczytywanie = null; });
  return wczytywanie;
}

function podmien(p: ChatQuestion): void {
  const jest = stan.pytania.some((x) => x.id === p.id);
  ustaw({ ...stan, pytania: jest ? stan.pytania.map((x) => (x.id === p.id ? p : x)) : [p, ...stan.pytania] });
}

/** Wysłanie pytania (nowa rozmowa: do = 0). 429 - limit z serwera trafia do stanu, błąd idzie dalej. */
export async function zapytaj(z: ChatIn): Promise<ChatQuestion> {
  try {
    const o = await zapytanie<ChatSent>("POST", "/api/czat", z);
    ustaw({ ...stan, limit: o.limit, limitOd: Date.now() });
    podmien(o.pytanie);
    return o.pytanie;
  } catch (b) {
    if (b instanceof BladZaplecza && b.status === 429) {
      const d = b.dane as ChatLimitReply | null;
      if (d?.limit) ustaw({ ...stan, limit: d.limit, limitOd: Date.now() });
    }
    throw b;
  }
}

/** Ocena odpowiedzi: od razu w stanie, zapis w tle (jak stara APP - wynik nie zmienia ekranu). */
export function ocenOdpowiedz(id: number, dobra: boolean): void {
  const p = stan.pytania.find((x) => x.id === id);
  if (p) podmien({ ...p, ocena: dobra ? 1 : -1 });
  void zapytanie<OKReply>("POST", `/api/czat/${id}/ocena`, { dobra }).catch(() => undefined);
}

/** Ocena wpisu bazy rozwiniętego w „Podobne pytania w bazie” / „Przykładowe pytania”. */
export const ocenWpis = (id: number, dobra: boolean) =>
  void zapytanie<OKReply>("POST", `/api/czat/baza/${id}/ocena`, { dobra }).catch(() => undefined);

/** Odpowiedź pokazana na ekranie - bez pusha „Asystent odpowiedział”. */
export const widziane = (id: number) => void zapytanie<OKReply>("POST", `/api/czat/${id}/widziane`).catch(() => undefined);

/** Podobne wpisy bazy do wpisywanego pytania; z tematem - tylko wpisy tematu (#15). */
export const szukajWBazie = (q: string, temat: string) =>
  zapytanie<ChatSearchReply>("GET", "/api/czat/szukaj?" + new URLSearchParams({ q, temat }).toString()).then((o) => o.wpisy);

export const cykleCzatu = () => zapytanie<ChatCyclesReply>("GET", "/api/czat/cykle").then((o) => o.cykle);

let sciezki: Promise<HelpReply> | null = null;
/** „Pomoc krok po kroku” - stała treść, raz na uruchomienie APP. */
export function pomocKrokPoKroku(): Promise<HelpReply> {
  sciezki ??= zapytanie<HelpReply>("GET", "/api/czat/sciezki").catch((b: unknown) => { sciezki = null; throw b; });
  return sciezki;
}

/** Zdarzenie SSE `konto`: tekst odpowiedzi na żywo. Pytanie zadane na innym telefonie - wczytanie rozmów. */
export function przyjmijZdarzenieCzatu(e: AccountEvent): void {
  if (e.rodzaj !== "czat.tekst" || !stan.wczytane) return;
  const z = e.dane as ChatLive;
  const p = stan.pytania.find((x) => x.id === z.id);
  if (!p) {
    void wczytajCzat().catch(() => undefined);
    return;
  }
  if (z.stan === "czeka") {
    podmien({ ...p, czesc: z.tekst });
    return;
  }
  if (z.stan === "gotowa") podmien({ ...p, stan: z.stan, odpowiedz: z.tekst, czesc: "" });
  else podmien({ ...p, stan: z.stan, powod: z.tekst, czesc: "" });
  // Gotowa odpowiedź - film pod nią dobiera worker przy odczycie rozmów.
  void wczytajCzat().catch(() => undefined);
}

/** Wylogowanie: bez rozmów poprzedniego konta. */
export function wyczyscCzat(): void {
  filtr = { szukaj: "", temat: "" };
  ustaw(pusty);
}
